import { decodeGlyphPath, generateGlyphCacheKey } from './GlyphPathDecoder';
import { CanvasLogger } from '../../../../../utils/loggers/CanvasLogger';

/**
 * GlyphDataProcessor - Utility for processing and caching glyph path data from WASM
 *
 * Handles the decoding, caching, and re-rendering logic for glyph paths received
 * from the Typesetter Web Worker.
 */
export class GlyphDataProcessor {
  /**
   * Process and cache glyph data received from worker
   *
   * This method handles the complete pipeline for glyph data:
   * 1. Error handling
   * 2. Binary data validation
   * 3. Glyph path decoding from WASM format
   * 4. Cache key generation
   * 5. Global glyph cache storage
   * 6. Optional re-render triggering via callback
   *
   * @param payload - Raw glyph data payload from worker message
   * @param glyphCacheCallback - Optional callback to trigger when glyph is cached
   */
  static processAndCacheGlyphData(
    payload: any,
    glyphCacheCallback?: () => void
  ): void {
    const { fontUid, gid, sizePx, buffer, bytes, error } = payload;

    if (error) {
      console.error('Glyph path error:', error);
      return;
    }

    if (buffer && bytes) {
      CanvasLogger.glyph.received(fontUid, sizePx, gid, buffer.byteLength);

      // Decode the binary glyph path data from WASM format
      const decoded = decodeGlyphPath(buffer);
      const ops = decoded.operations;
      const isEmptyGlyph = decoded.isEmpty;

      CanvasLogger.glyph.decoded(gid, ops.length, isEmptyGlyph);

      // Generate cache key using quantized size
      const k = generateGlyphCacheKey(fontUid, sizePx, gid);
      (globalThis as any).__edg_glyphOps = (globalThis as any).__edg_glyphOps || new Map();
      (globalThis as any).__edg_glyphOps.set(k, isEmptyGlyph ? Symbol.for('empty-glyph') : ops);
      CanvasLogger.glyph.cached(k, isEmptyGlyph ? 'empty' : ops.length);

      // Trigger incremental re-render if callback is set
      if (glyphCacheCallback) {
        // Debounce to prevent too frequent re-renders
        if (!(globalThis as any).__glyphRenderTimeout) {
          (globalThis as any).__glyphRenderTimeout = setTimeout(() => {
            delete (globalThis as any).__glyphRenderTimeout;
            CanvasLogger.glyph.rerender();
            glyphCacheCallback();
          }, 100); // 100ms debounce
        }
      }
    }
  }
}
